import { Helmet } from 'react-helmet'
import { Box, Button, Card, CircularProgress, Container, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useAppSelector } from '../../../store'
import { selectRestaurantIsSubscribed, selectLogin } from '../../../store/authSlice'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { ROUTES } from '../../../helpers/routes-helper'
import { createCheckoutSession, PlanName } from '../../../helpers/billing-helper'
import { MuiAlert, MuiSnackbar } from '../../eater/styled/global.styled'
import { setMessage, setOpen, setSeverity } from '../../../store/restaurantSlice'
import { useDevice } from '../../../hooks/useDevice'
import { errorLog } from '../../../utils/log-helper'
import { getReferralAttribution, clearReferralAttribution } from '../../../helpers/referral-attribution-helper'

const SUBSCRIPTION_WEB_URL = 'https://www.resactly.com/restaurant/subscriptions' // TODO: set real URL

const RestaurantSubscriptionPage: React.FC = () => {
  const { navigate, dispatch } = useAppUtilities()
  const { isHybrid } = useDevice()
  const isSubscribed = useAppSelector(selectRestaurantIsSubscribed)
  const login = useAppSelector(selectLogin)
  const restaurant = login?.restaurantDTO
  const restaurantName = restaurant?.name ?? 'Your Restaurant'
  const restaurantPoints = (restaurant as any)?.points ?? 0
  const [isLoading, setIsLoading] = useState(false)
  const message = useAppSelector((state) => state.restaurant.message)
  const severity = useAppSelector((state) => state.restaurant.severity)
  const openSnackbar = useAppSelector((state) => state.restaurant.shouldOpen)

  const openRestaurantSubscriptionWeb = (): void => {
    window.location.href = SUBSCRIPTION_WEB_URL
  }

  const handleSubscribe = async (): Promise<void> => {
    if (isHybrid) {
      // Hybrid/mobile app: send to external web portal
      openRestaurantSubscriptionWeb()
      return
    }

    // Web: use Stripe checkout
    setIsLoading(true)
    try {
      const planName: PlanName = 'explorer' // restaurant default (starter tier removed)
      const restaurantId = restaurant?.restaurantId ?? null

      // Get referral attribution if available
      let referrerEaterId: string | null = null
      let referrerRestaurantId: string | null = null
      
      const attribution = getReferralAttribution()
      if (attribution) {
        referrerEaterId = attribution.referrerEaterId ?? null
        referrerRestaurantId = attribution.referrerRestaurantId ?? null
      }

      const checkoutUrl = await createCheckoutSession(
        planName,
        null,          // eaterId not used here
        referrerEaterId,
        referrerRestaurantId,
        restaurantId,  // restaurantId
      )

      // Clear attribution after successful checkout session creation
      clearReferralAttribution()

      window.location.href = checkoutUrl
    } catch (error) {
      errorLog('Error creating checkout session', error)
      dispatch(setOpen(true))
      dispatch(setMessage('Failed to start checkout. Please try again.'))
      dispatch(setSeverity('error'))
      // Do NOT clear attribution on error so user can retry
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToPoints = (): void => {
    navigate(ROUTES.RESTAURANT_LEADERBOARD)
  }

  const handleCloseSnackbar = (): void => {
    dispatch(setOpen(false))
  }

  return (
    <Container component="main" sx={{ flex: 1, py: { xs: 3, md: 5 } }}>
      <Helmet><title>Resactly | Restaurant Subscription</title></Helmet>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Header */}
        <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4 }}>
          Subscription & Rewards
        </Typography>

        {/* Top card: Restaurant name and points */}
        <Card
          sx={{
            mb: 4,
            borderRadius: '16px',
            backgroundColor: 'secondary.50',
            p: 3,
          }}
        >
          <Typography variant="h6" component="div" gutterBottom>
            {restaurantName}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              component="img"
              src="/images/leaderboard/coin-yellow.png"
              alt="Points"
              sx={{ width: 40, height: 40 }}
            />
            <Typography variant="h5" component="div">
              {restaurantPoints.toLocaleString()} Points
            </Typography>
          </Box>
        </Card>

        {/* Main content */}
        {!isSubscribed ? (
          <Card
            sx={{
              borderRadius: '16px',
              backgroundColor: 'background.paper',
              p: 4,
            }}
          >
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
              Subscribe to Resactly
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {isHybrid
                ? "We'll open our web portal in your browser so you can start your subscription."
                : "Start your restaurant subscription to unlock analytics, rewards, and more."}
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 4 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                Access advanced analytics and insights
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                Redeem points for rewards and benefits
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                Enhanced visibility and marketing tools
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
                Cancel anytime
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => { void handleSubscribe()}}
              disabled={isLoading}
              sx={{ py: 1.5 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Subscribe to Resactly'
              )}
            </Button>
          </Card>
        ) : (
          <Card
            sx={{
              borderRadius: '16px',
              backgroundColor: 'background.paper',
              p: 4,
            }}
          >
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
              Your restaurant is currently subscribed.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              You can manage your subscription and redeem points from the points screen.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleBackToPoints}
              sx={{ py: 1.5 }}
            >
              Back to Points
            </Button>
          </Card>
        )}
      </Box>

      <MuiSnackbar
        open={openSnackbar}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={severity}
        >
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </Container>
  )
}

export default RestaurantSubscriptionPage

